import React, { useRef, useState } from 'react'
import cn from 'classnames'
import useIsomorphicLayoutEffect from 'use-isomorphic-layout-effect'
import { Sticky, StickyControlled } from '@mc/design-system'
import { Button, ButtonProps } from './Button'
import classes from './Cta.module.scss'

type CtaProps = ButtonProps & {
  sticky?: {
    alwaysVisible?: boolean
    containerClassName?: string
    onFirstVisible?: () => void
    hideInline?: boolean
  }
}

export const CTA_STICKY_ID = 'plans-ui-sticky-cta'
export const CTA_FIXED_ID = 'plans-ui-fixed-cta'
export const Cta = ({
  ariaLabel,
  text,
  disabled,
  onClick,
  loading,
  sticky = {
    alwaysVisible: false,
    containerClassName: '',
    onFirstVisible: () => {},
  },
}: CtaProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [nonStickyButtonPosition, setNonStickyButtonPosition] =
    useState<number>(0)

  useIsomorphicLayoutEffect(() => {
    const nonStickyButton = buttonRef?.current
    if (nonStickyButton) {
      const topPosition = nonStickyButton?.getBoundingClientRect()?.top
      if (topPosition > 0) {
        const buttonPosition = topPosition - window.innerHeight
        if (buttonPosition > 0) {
          setNonStickyButtonPosition(buttonPosition)
        }
      }
    }
  }, [])

  const stickyButton = (
    <Button
      id={CTA_STICKY_ID}
      ariaLabel={ariaLabel}
      text={text}
      disabled={disabled}
      onClick={onClick}
      isSticky
      loading={loading}
      onFirstVisible={sticky.onFirstVisible}
    />
  )

  const inlineButton = (
    <Button
      id={CTA_FIXED_ID}
      ref={buttonRef}
      ariaLabel={ariaLabel}
      text={text}
      disabled={disabled}
      onClick={onClick}
      loading={loading}
    />
  )

  return (
    <>
      {sticky.alwaysVisible ? (
        <StickyControlled show className={classes.stickyBackgroundSolid}>
          <div className={cn('container', sticky.containerClassName)}>
            {stickyButton}
          </div>
        </StickyControlled>
      ) : (
        <div className='d-block d-lg-none'>
          <Sticky
            className={classes.stickyBackground}
            showAtPosition={0}
            hideAtPosition={nonStickyButtonPosition}
          >
            {stickyButton}
          </Sticky>
        </div>
      )}
      {!sticky.hideInline && inlineButton}
    </>
  )
}
